import React from 'react';
import { AimieRobotAvatarIcon, UserAvatarIcon } from '../ChatAvatarIcons';
import ContemplatingIndicator from '../ContemplatingIndicator';
import ModeSelector from '../ModeSelector';
import StimulusAnnotationText from '../oars/StimulusAnnotationText';
import './SpecificStrategiesPracticePane.css';

function SpecificStrategiesPracticePane({
  t,
  theme = 'dark',
  activeMode,
  onFreeForm,
  onBeTheClient,
  onOars,
  onStrategies,
  strategiesFlow,
  stimulusAnnotations = [],
  suppressAnnotationTooltips = false,
  showInlineModeSelector = false,
  hideEmptyStateTitle = false,
  hideEmptyStateDescription = false,
  singleColumnEmptyPrompt = '',
  userAvatarPreference,
  messagesContainerRef,
  messagesEndRef,
}) {
  const {
    exercise,
    conversation = [],
    status,
    loading,
    error,
    viewedTurnIndex = null,
  } = strategiesFlow || {};
  const turnAnchorRefs = React.useRef({});
  const submittedTurnCount = conversation.filter(
    (turn, index) => index >= (exercise?.turn_count || 0) && turn.speaker === 'counsellor',
  ).length;

  React.useEffect(() => {
    if (viewedTurnIndex === null) return;
    const container = messagesContainerRef?.current;
    const target = turnAnchorRefs.current[viewedTurnIndex];
    if (!container || !target) return;
    const containerTop = container.getBoundingClientRect().top;
    const targetTop = target.getBoundingClientRect().top;
    container.scrollTo({
      top: container.scrollTop + targetTop - containerTop - 12,
      behavior: 'smooth',
    });
  }, [messagesContainerRef, viewedTurnIndex]);

  if (loading && !exercise) {
    return (
      <div className="messages-container oars-messages oars-messages--loading" ref={messagesContainerRef} aria-live="polite">
        <div className="oars-session-position-indicator oars-session-position-indicator--loading">
          <span className="oars-session-position-dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <span className="oars-session-position-text">Preparing Scaling Questions exercise</span>
        </div>

        <div className="message counsellor oars-loading-message">
          <div className="oars-loading-bubble oars-loading-bubble--wide">
            <span className="oars-loading-line oars-loading-line--long" />
            <span className="oars-loading-line oars-loading-line--mid" />
          </div>
        </div>
        <div className="message client oars-loading-message">
          <div className="oars-loading-bubble oars-loading-bubble--client">
            <span className="oars-loading-line oars-loading-line--mid" />
            <span className="oars-loading-line oars-loading-line--short" />
          </div>
        </div>
        <div className="message counsellor oars-loading-message">
          <div className="oars-loading-bubble oars-loading-bubble--wide">
            <span className="oars-loading-line oars-loading-line--long" />
            <span className="oars-loading-line oars-loading-line--mid" />
            <span className="oars-loading-line oars-loading-line--short" />
          </div>
        </div>
        <div className="oars-cut-indicator oars-cut-indicator--loading">
          <span className="oars-cut-line" />
          <span className="oars-cut-label">Preparing your next turn...</span>
          <span className="oars-cut-line" />
        </div>
        <div ref={messagesEndRef} />
      </div>
    );
  }

  if (!exercise) {
    return (
      <div className="chat-empty-state entrance">
        <div className="chat-empty-copy">
          {!hideEmptyStateTitle && <h2>Specific Strategies</h2>}
          {!hideEmptyStateDescription && (
            <p>
              Specific Strategies gives you focused MI practice on one strategy at a time,
              without committing to a full conversation.
            </p>
          )}
          {singleColumnEmptyPrompt && (
            <p className="chat-empty-single-column-prompt">{singleColumnEmptyPrompt}</p>
          )}
          {error && <p className="specific-strategies-error">{error}</p>}
        </div>
        {showInlineModeSelector && (
          <div className="chat-empty-mode-selector-shell">
            <ModeSelector
              modeBarVisible
              setModeBarVisible={() => {}}
              activeMode={activeMode}
              onFreeForm={onFreeForm}
              onBeTheClient={onBeTheClient}
              onOars={onOars}
              onStrategies={onStrategies}
              variant="inline"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="messages-container oars-messages" ref={messagesContainerRef} aria-live="polite">
      <div className="oars-session-position-indicator">
        <span className="oars-session-position-dots" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
        <span className="oars-session-position-text">You are in the middle of a session</span>
      </div>

      {conversation.map((turn, index) => {
        const stimulusAnnotationsForTurn = stimulusAnnotations.filter(
          (annotation) => annotation.turn_id === `turn_${index}`,
        );
        const isTraineeTurn = turn.speaker === 'counsellor' && index >= exercise.turn_count;
        const practiceTurnIndex = index >= exercise.turn_count
          ? Math.floor((index - exercise.turn_count) / 2)
          : -1;
        const isViewingLatest = viewedTurnIndex === null || viewedTurnIndex === submittedTurnCount - 1;
        const isActiveMessage = isViewingLatest || practiceTurnIndex === viewedTurnIndex;
        const isPracticeTurnAnchor = index >= exercise.turn_count && (index - exercise.turn_count) % 2 === 0;
        return (
        <div
          key={`${turn.speaker}-${index}`}
          ref={isPracticeTurnAnchor ? (node) => { turnAnchorRefs.current[practiceTurnIndex] = node; } : null}
          className={`message ${turn.speaker}${!isActiveMessage ? ' message-faded' : ''}`}
        >
          {turn.speaker === 'client' && (
            <span className="message-avatar message-avatar--robot" aria-hidden="true">
              <AimieRobotAvatarIcon />
            </span>
          )}
          <div className="message-bubble-shell">
            {turn.speaker === 'client' && turn.isStreaming && !turn.text
              ? <ContemplatingIndicator />
              : (
                turn.speaker === 'client'
                  ? <StimulusAnnotationText
                    text={turn.text}
                    annotations={stimulusAnnotationsForTurn}
                    theme={theme}
                    t={t}
                    suppressed={suppressAnnotationTooltips}
                  />
                  : <div className={turn.speaker === 'client' ? `message-text-plain${turn.isStreaming ? ' streaming' : ''}` : `message-text${isTraineeTurn ? ' message-text--user' : ''}`}>
                    {turn.text}
                  </div>
              )}
          </div>
          {turn.speaker === 'counsellor' && (
            <span className="message-avatar message-avatar--human" aria-hidden="true">
              <UserAvatarIcon {...userAvatarPreference} />
            </span>
          )}
        </div>
        );
      })}

      {status === 'active' && !loading && (
        <div className="oars-cut-indicator">
          <span className="oars-cut-line" />
          <span className="oars-cut-label">Your turn: what would you say next?</span>
          <span className="oars-cut-line" />
        </div>
      )}

      {error && <p className="specific-strategies-error">{error}</p>}
      <div ref={messagesEndRef} />
    </div>
  );
}

export default SpecificStrategiesPracticePane;
